import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminGetUsers, adminCreateUser, adminUpdateUser, adminDeleteUser, AdminUser } from '../../utils/remote';

const AdminUsers: React.FC = () => {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [search, setSearch] = useState('');

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['admin-users', page, pageSize, search],
    queryFn: () => adminGetUsers({ page, page_size: pageSize, search: search.trim() || undefined }),
    // v5: use placeholderData to keep previous page data during transitions
    placeholderData: (prev) => prev,
    staleTime: 5_000,
  });

  const createMut = useMutation({
    mutationFn: adminCreateUser,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Partial<AdminUser> & { password?: string } }) => adminUpdateUser(id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      setEditOpen(false);
      setEditingUser(null);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => adminDeleteUser(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });

  const [form, setForm] = useState({
    username: '',
    password: '',
    email: '',
    first_name: '',
    last_name: '',
    is_superuser: false,
    is_active: true,
  });

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [editForm, setEditForm] = useState<{ username: string; email: string; first_name: string; last_name: string; is_active: boolean; is_superuser: boolean; password?: string }>({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    is_active: true,
    is_superuser: false,
    password: '',
  });

  const openEdit = (u: AdminUser) => {
    setEditingUser(u);
    setEditForm({
      username: u.username,
      email: u.email || '',
      first_name: u.first_name || '',
      last_name: u.last_name || '',
      is_active: !!u.is_active,
      is_superuser: !!u.is_superuser,
      password: '',
    });
    setEditOpen(true);
  };

  const submitEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    const patch: any = {
      username: editForm.username?.trim() || editingUser.username,
      email: editForm.email?.trim() || '',
      first_name: editForm.first_name?.trim() || '',
      last_name: editForm.last_name?.trim() || '',
      is_active: editForm.is_active,
      is_superuser: editForm.is_superuser,
    };
    if (editForm.password && editForm.password.length >= 6) {
      patch.password = editForm.password;
    }
    updateMut.mutate({ id: editingUser.id, patch });
  };

  const total = data?.count ?? 0;
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  const onCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.username.trim()) return;
    createMut.mutate({
      username: form.username.trim(),
      password: form.password || undefined,
      email: form.email || undefined,
      first_name: form.first_name || undefined,
      last_name: form.last_name || undefined,
      is_superuser: form.is_superuser,
      is_active: form.is_active,
    });
    setForm({ username: '', password: '', email: '', first_name: '', last_name: '', is_superuser: false, is_active: true });
  };

  const users = data?.results ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-black dark:text-white">Manage Users</h1>
        <div className="text-sm text-body">{isFetching ? 'Refreshing…' : ''}</div>
      </div>

      {/* Search */}
      <div className="flex gap-2">
        <input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by username, email, or name"
          className="w-full rounded border border-stroke bg-transparent p-2 text-black outline-none focus:border-primary dark:border-form-strokedark dark:bg-form-input dark:text-white"
        />
        <button onClick={() => refetch()} className="rounded border border-primary bg-primary px-4 py-2 text-white">Search</button>
      </div>

      {/* Create user */}
      <form onSubmit={onCreate} className="rounded border border-stroke p-4 dark:border-strokedark">
        <h2 className="mb-3 font-semibold">Add User</h2>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
          <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="Username" className="rounded border border-stroke bg-transparent p-2 dark:border-form-strokedark dark:bg-form-input" />
          <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password" className="rounded border border-stroke bg-transparent p-2 dark:border-form-strokedark dark:bg-form-input" />
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="rounded border border-stroke bg-transparent p-2 dark:border-form-strokedark dark:bg-form-input" />
          <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="First name" className="rounded border border-stroke bg-transparent p-2 dark:border-form-strokedark dark:bg-form-input" />
          <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Last name" className="rounded border border-stroke bg-transparent p-2 dark:border-form-strokedark dark:bg-form-input" />
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_superuser} onChange={(e) => setForm({ ...form, is_superuser: e.target.checked })} /> Superuser</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
          </div>
        </div>
        <div className="mt-3">
          <button type="submit" disabled={createMut.isPending} className="rounded border border-primary bg-primary px-4 py-2 text-white disabled:opacity-60">{createMut.isPending ? 'Creating…' : 'Create User'}</button>
          {createMut.isError && <span className="ml-3 text-red-600 text-sm">Failed: {(createMut.error as any)?.response?.data ? JSON.stringify((createMut.error as any).response.data) : (createMut.error as any)?.message}</span>}
        </div>
      </form>

      {/* Users table */}
      <div className="rounded border border-stroke dark:border-strokedark overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100 dark:bg-boxdark-2">
            <tr>
              <th className="p-2 text-left">ID</th>
              <th className="p-2 text-left">Username</th>
              <th className="p-2 text-left">Name</th>
              <th className="p-2 text-left">Email</th>
              <th className="p-2 text-left">Active</th>
              <th className="p-2 text-left">Superuser</th>
              <th className="p-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td className="p-3" colSpan={7}>Loading…</td></tr>
            ) : isError ? (
              <tr><td className="p-3 text-red-600" colSpan={7}>Error loading users: {(error as any)?.message || 'Unknown error'}</td></tr>
            ) : users.length === 0 ? (
              <tr><td className="p-3" colSpan={7}>No users</td></tr>
            ) : (
              users.map((u) => (
                <tr key={u.id} className="border-t border-stroke dark:border-strokedark">
                  <td className="p-2">{u.id}</td>
                  <td className="p-2 font-mono">{u.username}</td>
                  <td className="p-2">{u.first_name} {u.last_name}</td>
                  <td className="p-2">{u.email}</td>
                  <td className="p-2">
                    <button
                      className={`rounded px-2 py-1 text-white ${u.is_active ? 'bg-green-600' : 'bg-gray-500'}`}
                      onClick={() => updateMut.mutate({ id: u.id, patch: { is_active: !u.is_active } })}
                    >{u.is_active ? 'Active' : 'Inactive'}</button>
                  </td>
                  <td className="p-2">
                    <button
                      className={`rounded px-2 py-1 text-white ${u.is_superuser ? 'bg-purple-600' : 'bg-gray-500'}`}
                      onClick={() => updateMut.mutate({ id: u.id, patch: { is_superuser: !u.is_superuser } })}
                    >{u.is_superuser ? 'Yes' : 'No'}</button>
                  </td>
                  <td className="p-2 flex gap-2">
                    <button
                      className="rounded border border-stroke px-2 py-1 hover:bg-gray-100 dark:hover:bg-boxdark-2"
                      onClick={() => openEdit(u)}
                    >Edit</button>
                    <button
                      className="rounded border border-red-600 text-red-600 px-2 py-1 hover:bg-red-50"
                      onClick={() => { if (confirm(`Delete ${u.username}?`)) deleteMut.mutate(u.id); }}
                    >Delete</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm">Total: {total}</div>
        <div className="flex gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded border border-stroke px-3 py-1 disabled:opacity-50">Prev</button>
          <span className="px-2">Page {page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="rounded border border-stroke px-3 py-1 disabled:opacity-50">Next</button>
        </div>
      </div>

      {/* Edit Dialog */}
      {editOpen && editingUser && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl rounded-lg border border-stroke bg-white p-5 shadow-lg dark:border-strokedark dark:bg-boxdark">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-black dark:text-white">Edit User: <span className="font-mono">{editingUser.username}</span></h3>
              <button onClick={() => { setEditOpen(false); setEditingUser(null); }} className="rounded px-2 py-1 text-body hover:text-black dark:hover:text-white">✕</button>
            </div>
            <form onSubmit={submitEdit} className="space-y-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm">Username</label>
                  <input
                    value={editForm.username}
                    onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                    className="w-full rounded border border-stroke bg-transparent p-2 dark:border-form-strokedark dark:bg-form-input"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm">Email</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    className="w-full rounded border border-stroke bg-transparent p-2 dark:border-form-strokedark dark:bg-form-input"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm">First name</label>
                  <input
                    value={editForm.first_name}
                    onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                    className="w-full rounded border border-stroke bg-transparent p-2 dark:border-form-strokedark dark:bg-form-input"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm">Last name</label>
                  <input
                    value={editForm.last_name}
                    onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                    className="w-full rounded border border-stroke bg-transparent p-2 dark:border-form-strokedark dark:bg-form-input"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm">New Password (optional)</label>
                  <input
                    type="password"
                    placeholder="Leave blank to keep current"
                    value={editForm.password}
                    onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                    className="w-full rounded border border-stroke bg-transparent p-2 dark:border-form-strokedark dark:bg-form-input"
                  />
                  <p className="mt-1 text-xs text-body">Min 6 characters if provided.</p>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editForm.is_active} onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })} /> Active</label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editForm.is_superuser} onChange={(e) => setEditForm({ ...editForm, is_superuser: e.target.checked })} /> Superuser</label>
                </div>
              </div>

              <div className="mt-2 flex justify-end gap-2">
                <button type="button" onClick={() => { setEditOpen(false); setEditingUser(null); }} className="rounded border border-stroke px-4 py-2 hover:bg-gray-50 dark:hover:bg-boxdark-2">Cancel</button>
                <button type="submit" disabled={updateMut.isPending} className="rounded border border-primary bg-primary px-4 py-2 text-white disabled:opacity-60">{updateMut.isPending ? 'Saving…' : 'Save Changes'}</button>
              </div>
              {updateMut.isError && <div className="text-sm text-red-600">Failed to update: {(updateMut.error as any)?.response?.data ? JSON.stringify((updateMut.error as any).response.data) : (updateMut.error as any)?.message}</div>}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
